<template>
  <nav class="navbar" role="navigation" aria-label="main navigation">
    <div class="navbar-brand">
      <router-link class="" to="/">
        <div class="logo"></div>
      </router-link>
    </div>
    <ul class="tabs">
      <li>
        <!-- A-9: доступ к вкладке Live по праву LIVE_VIEW -->
        <NavTabLock permission="LIVE_VIEW" to="/live">
          {{ $t("live") }}
        </NavTabLock>
      </li>
      <li>
        <!-- A-9: доступ к вкладке Prematch по праву PREMATCH_VIEW -->
        <NavTabLock permission="PREMATCH_VIEW" to="/prematch">
          {{ $t("prematch") }}
        </NavTabLock>
      </li>
      <li>
        <!-- A-9: доступ к вкладке Ticker по праву TICKER_VIEW -->
        <NavTabLock permission="TICKER_VIEW" to="/ticker">
          {{ $t("ticker") }}
        </NavTabLock>
      </li>
      <li>
        <router-link
          class="tab-link"
          to="/subscriptions"
          :exact="'/subscriptions' === '/'"
        >
          {{ $t("subscription") }}
        </router-link>
      </li>
    </ul>

    <div class="navbar-end">
      <!-- <Search /> -->
      <LangSwitch is-short />
      <ThemeSwitcherHeader />
      <UserMenu :user="user" />
      <a
        class="navbar-burger"
        role="button"
        aria-label="menu"
        aria-expanded="false"
        @click="toggleMenu"
      >
        <div class="item_burger"></div>
        <div class="item_burger"></div>
      </a>
    </div>

    <a
      class="navbar-burger mob-burger"
      role="button"
      aria-label="menu"
      aria-expanded="false"
      @click="toggleMenu"
    >
      <div class="item_burger"></div>
      <div class="item_burger"></div>
    </a>

    <MobileMenu :is-active="isMenuActive" @close-menu="toggleMenu" />
  </nav>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";

import ThemeSwitcherHeader from "./ThemeSwitcherHeader.vue";
import MobileMenu from "./MobileMenu.vue";
import UserMenu from "./common/UserMenu.vue";
// import Search from "./common/Search.vue";
import LangSwitch from "./LangSwitch.vue";
import NavTabLock from "./NavTabLock.vue";

const store = useStore();

const userTheme = ref("light-theme");
const isMenuActive = ref(false);

const user = computed(() => store.state.user);

const getTheme = () => {
  return localStorage.getItem("user-theme");
};

const setTheme = (theme) => {
  localStorage.setItem("user-theme", theme);
  userTheme.value = theme;
  document.documentElement.className = theme;
};

const getMediaPreference = () => {
  const hasDarkPreference = window.matchMedia(
    "(prefers-color-scheme: dark)",
  ).matches;
  return hasDarkPreference ? "dark-theme" : "light-theme";
};

const toggleMenu = () => {
  isMenuActive.value = !isMenuActive.value;
};

setTheme(getTheme() || getMediaPreference());
</script>

<style lang="scss" scoped>
.navbar {
  height: 100%;
  background-color: var(--body-color);
  border-bottom: 1px solid var(--footer-color);
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .navbar-burger {
    background-color: var(--league-body-color);
    display: none;
    flex-direction: column;
    height: max-content;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 30px 10px 30px;
    width: max-content;
    border-radius: 100px;
    height: 40px;

    .item_burger {
      width: 30px;
      height: 2px;
      background-color: var(--font-color);
    }

    @media screen and (max-width: $desktop) {
      display: flex;
    }
  }

  .navbar-end {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;

    @media screen and (max-width: $tablet) {
      display: none;
    }
  }

  .mob-burger {
    display: none;

    @media screen and (max-width: $tablet) {
      display: flex;
    }
  }

  .navbar-burger.is-active span:nth-child(2) {
    opacity: 1;
    transform: translateY(0px) rotate(136deg);
  }

  .navbar-brand {
    height: 100%;
    align-items: center;

    .navbar-burger {
      color: var(--font-color);

      .is-active span:nth-child(2) {
        opacity: 1 !important;
        transform: translateY(0px) rotate(133deg) !important;
      }
    }

    a {
      opacity: 1;
      transition: all 0.2s;

      &:hover {
        opacity: 0.6;
      }

      .logo {
        display: block;
        font-weight: 400;
        text-transform: uppercase;
        font-size: 32px;
        line-height: 39.68px;
        padding-bottom: 29px;
        padding-top: 30px;

        &:before {
          content: "SCORE";
          color: var(--font-color);
        }

        &:after {
          content: "68";
          color: $successColor;
        }

        .beta {
          font-size: 0.6rem;
          position: absolute;
          top: 0.25rem;
          right: 0;
        }
      }
    }
  }

  .tabs {
    display: flex;
    background-color: var(--body-color);
    height: 100%;
    border: none;

    @media screen and (max-width: $desktop) {
      display: none;
    }

    li {
      height: 100%;

      .tab-link {
        position: relative;
        height: 100%;
        padding: 8px 16px;
        color: var(--font-color);
        border-bottom: 3px solid transparent;
        transition: all 0.2s;
        text-transform: uppercase;

        &.router-link-active {
          color: var(--brand-color);
        }
      }

      a {
        font-weight: 400;
        font-size: 16px;
        line-height: 19.84px;
        letter-spacing: 2px;
      }
    }
  }

  .navbar-end {
    align-items: center;
    gap: 20px;

    .dark path {
      stroke: var(--font-color);
    }

    .login {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 12px 24px 12px 24px;
      gap: 12px;
      border-radius: 150px;
      outline: none;
      border: none;
      font-size: 16px;
      line-height: 16px;
      letter-spacing: 1px;
      color: var(--font-color);
      font-weight: 400;
      background-color: var(--league-body-color);
      cursor: pointer;
    }

    .user {
      text-overflow: ellipsis;
      overflow: hidden;
      max-width: 35px;
    }
  }
}
</style>
